import React, { useState, useEffect } from "react";
import { ChevronLeft, Briefcase, Clock, Users } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { useWorker } from "../context/WorkerContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CompanyDepartmentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useWorker();

  const company = location.state?.company;

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!company) {
      // No company passed in (e.g. direct refresh) - nothing to show, go back
      navigate(-1);
      return;
    }
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const fetchDepartments = async () => {
    if (!user?.skills || user.skills.length === 0) {
      toast.error("Please select your skills first.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/onDemandCompanies/get-department`,
        { companyId: company._id, skills: user.skills, skillIds: user.skillIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      toast.error("Failed to fetch departments");
    } finally {
      setLoading(false);
    }
  };

  if (!company) return null;

  return (
    <div className="bg-white min-h-screen">
      <div className="top-0 z-20 bg-white flex items-center justify-center relative px-4 py-4 border-b border-gray-100 pt-20">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 p-1 -ml-1 text-gray-900 hover:text-gray-600 transition-colors"
        >
          <ChevronLeft className="h-7 w-7" strokeWidth={2.5} />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Departments & Shift</h1>
      </div>

      <div className="px-4 pt-6 pb-10">
        {/* Company info card */}
        <div className="bg-[#E4E3FB] rounded-2xl p-5 mb-6 flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
            {company.logo ? (
              <img
                src={company.logo}
                alt={company.name}
                className="w-8 h-8 object-contain"
              />
            ) : (
              <span className="text-2xl">🏭</span>
            )}
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              {company.name}
            </h2>
            <p className="text-sm text-gray-700 leading-snug">
              {company.address}
            </p>
          </div>
        </div>

        {/* Departments */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading departments...</p>
            </div>
          </div>
        ) : departments.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Departments Found
            </h3>
            <p className="text-sm text-gray-500">
              There are no matching departments for your skills right now.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {departments.map((dept, index) => (
              <div key={index}>
                {/* Department label */}
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-5 h-5 text-gray-800" strokeWidth={1.75} />
                  <div>
                    <p className="text-xs text-gray-500 leading-tight">Department</p>
                    <p className="text-base font-semibold text-gray-900 leading-tight">
                      {dept.department}
                    </p>
                  </div>
                </div>

                {/* Shift slots card */}
                <div className="border border-gray-200 rounded-2xl p-4">
                  <h5 className="text-sm font-bold text-indigo-700 mb-1">
                    Shift Slots:
                  </h5>
                  <p className="text-xs text-gray-500 mb-4">
                    *Minimum 4 hours required in a shift
                  </p>

                  {dept.timeSlots && dept.timeSlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                      {dept.timeSlots.map((slot, slotIndex) => (
                        <div key={slotIndex} className="flex gap-2.5">
                          <div className="w-[3px] rounded-full bg-indigo-700 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-bold text-indigo-700 leading-tight">
                              {slot.slotShift}
                            </p>
                            <p className="text-sm text-gray-600 leading-tight mt-0.5">
                              {slot.slotStart} - {slot.slotEnd}
                            </p>
                            {slot.slotPay && (
                              <p className="text-xs font-medium text-green-600 mt-0.5">
                                ₹{slot.slotPay}/hr
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">
                      No time slots available
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDepartmentsPage;